import { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// VAPID public key from environment
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function PushNotificationManager({ user }) {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [permission, setPermission] = useState('default');
  const [deniedHidden, setDeniedHidden] = useState(() => localStorage.getItem('woma_notif_denied_hidden') === '1');
  const hideDenied = () => {
    localStorage.setItem('woma_notif_denied_hidden', '1');
    setDeniedHidden(true);
  };

  useEffect(() => {
    // Check if push notifications are supported
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setIsSupported(true);
      checkSubscription();
      setPermission(Notification.permission);

      // Show banner if not subscribed and permission not denied
      if (Notification.permission !== 'denied' && Notification.permission !== 'granted') {
        const dismissed = localStorage.getItem('push-banner-dismissed');
        if (!dismissed) {
          setTimeout(() => setShowBanner(true), 3000);
        }
      }
    }
  }, []);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const subscribe = useCallback(async () => {
    if (!user) {
      toast.error('Connectez-vous pour activer les notifications');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;

      // Request permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        toast.error('Permission refusée pour les notifications');
        return;
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      // Send subscription to backend
      const subscriptionJSON = subscription.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        user_id: user.id,
        endpoint: subscriptionJSON.endpoint,
        keys: subscriptionJSON.keys
      });

      setIsSubscribed(true);
      setShowBanner(false);
      toast.success('🔔 Notifications activées!');
    } catch (error) {
      console.error('Error subscribing:', error);
      toast.error('Erreur lors de l\'activation des notifications');
    }
  }, [user]);

  const unsubscribe = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();

        if (user) {
          await axios.delete(`${API}/push/unsubscribe/${user.id}`);
        }
      }

      setIsSubscribed(false);
      toast.success('Notifications désactivées');
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast.error('Erreur lors de la désactivation');
    }
  }, [user]);

  const dismissBanner = () => {
    setShowBanner(false);
    localStorage.setItem('push-banner-dismissed', 'true');
  };

  if (!isSupported) return null;

  return (
    <>
      {/* Floating toggle button (when subscribed) */}
      {isSubscribed && (
        <button
          onClick={unsubscribe}
          className="fixed bottom-20 right-4 z-40 bg-gradient-to-br from-[#003594] to-[#D4AF37] text-white p-3 rounded-full shadow-lg hover:opacity-90 transition-all"
          title="Notifications activées - Cliquer pour désactiver"
        >
          <Bell className="w-5 h-5" />
        </button>
      )}

      {/* Permission banner */}
      {showBanner && !isSubscribed && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden animate-slide-up">
          <div className="bg-gradient-to-r from-[#003594] to-[#D4AF37] p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                <span className="font-semibold">Notifications</span>
              </div>
              <button
                onClick={dismissBanner}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="p-4">
            <p className="text-sm text-gray-600 mb-4">
              Activez les notifications pour recevoir des alertes sur vos commandes, le statut des livraisons et les promotions.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={subscribe}
                className="flex-1 bg-gradient-to-r from-[#003594] to-[#D4AF37] hover:opacity-90 text-white rounded-full font-semibold"
              >
                <Bell className="w-4 h-4 mr-2" />
                Activer
              </Button>
              <Button
                onClick={dismissBanner}
                variant="outline"
                className="rounded-full"
              >
                Plus tard
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Simple toggle for settings page */}
      {permission === 'denied' && !deniedHidden && (
        <div className="fixed bottom-4 right-3 z-20 bg-orange-100/95 text-orange-800 pl-2.5 pr-1 py-1 rounded-full shadow-lg text-[11px] flex items-center gap-1.5" data-testid="notif-blocked-badge">
          <BellOff className="w-4 h-4" />
          <span>Notifications bloquées</span>
          <button type="button" onClick={hideDenied} aria-label="Masquer" className="p-1 rounded-full hover:bg-orange-200" data-testid="notif-blocked-close-btn">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
}

// Hook pour utiliser les notifications push depuis d'autres composants
export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setIsSupported(true);

      navigator.serviceWorker.ready.then(async (registration) => {
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      });
    }
  }, []);

  return { isSupported, isSubscribed };
}
